import './addresses.scss';
import { useCallback, useState } from 'react';
import { Else, If, Then } from 'react-if';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAddresses from '../../../utilities/hooks/useAddresses';
import useRemoveAddress from '../../../utilities/hooks/useRemoveAddress';
import useSetPrimaryAddress from '../../../utilities/hooks/useSetPrimaryAddress';
import PageHeader from '../../shared/blocks/page-header/page-header';
import Loader from '../../shared/blocks/loader/loader';
import Modal from '../../shared/modals/modal';

const ManageAddresses = () => {
  const { userAddressesFetched } = useSelector((state) => state.accounts);
  const addresses = useAddresses();
  const [removingAddress, setRemovingAddress] = useState();

  const { remove: removeAddress, isLoading: isRemoving } = useRemoveAddress();
  const { set: setPrimaryAddress } = useSetPrimaryAddress();

  const handleRemoveAddress = useCallback(() => {
    const { id: addressId, isDefaultShippingAddress } = removingAddress;

    removeAddress(addressId, {
      onSuccess: () => {
        if (isDefaultShippingAddress) {
          let nextAddressId;
          if (addresses[0].id !== addressId) {
            nextAddressId = addresses[0].id;
          } else if (addresses[1]) {
            nextAddressId = addresses[1].id;
          }

          if (nextAddressId) {
            setPrimaryAddress(nextAddressId);
          }
        }
        setRemovingAddress(undefined);
      },
    });
  }, [addresses, removeAddress, removingAddress, setPrimaryAddress]);

  return (
    <div className="addresses-page">
      <PageHeader title="Addresses" />

      <div className="addresses-page__container">
        <If condition={userAddressesFetched}>
          <Then>
            <If condition={addresses?.length}>
              <Then>
                <div className="addresses-page__list">
                  {addresses.map((address) => (
                    <div
                      key={address.id}
                      className="addresses-page__list__item"
                    >
                      <div>
                        <h4 className="addresses-page__list__item__headline">
                          {address.additionalAddressInfo ||
                            `${address.firstName} ${address.lastName}`}
                        </h4>

                        <p className="addresses-page__list__item__paragraph">
                          {`${address.firstName} ${address.lastName}`}
                        </p>

                        <p className="addresses-page__list__item__paragraph">
                          {address.streetName}
                        </p>
                        <p className="addresses-page__list__item__paragraph">
                          {address.apartment}
                        </p>
                        <p className="addresses-page__list__item__paragraph">{`${address.city}, ${address.state} ${address.postalCode}`}</p>
                      </div>
                      <div className="addresses-page__list__item__actions">
                        <If condition={address.isDefaultShippingAddress}>
                          <Then>
                            <span className="addresses-page__list__item__actions__primary">
                              PRIMARY
                            </span>
                          </Then>
                        </If>
                        <div className="addresses-page__list__item__actions__links">
                          <If condition={!address.isDefaultShippingAddress}>
                            <Then>
                              <button
                                className="addresses-page__list__item__actions__link"
                                type="button"
                                onClick={() => setRemovingAddress(address)}
                              >
                                Remove
                              </button>
                            </Then>
                          </If>
                          <Link
                            className="addresses-page__list__item__actions__link"
                            to={`/addresses/edit/?id=${address.id}`}
                          >
                            Edit
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Then>

              <Else>
                <p className="addresses-page__no-address">
                  There are currently no addresses associated with your account.
                </p>
              </Else>
            </If>

            <Link to="/addresses/add">
              <button type="button" className="button button--primary">
                Add new address
              </button>
            </Link>
          </Then>

          <Else>
            <Loader />
          </Else>
        </If>
      </div>

      <If condition={removingAddress}>
        <Then>
          <Modal
            title="Are You Sure?"
            onCancel={() => setRemovingAddress(undefined)}
            onSubmit={handleRemoveAddress}
            submitText="Delete"
            cancelText="Cancel"
            submitting={isRemoving}
          >
            <p>Are you sure you want to delete this address?</p>
          </Modal>
        </Then>
      </If>
    </div>
  );
};

export default ManageAddresses;
